import React, { useState } from 'react';
import { Event } from '../types';
import { EventRegistrationModal } from '../components/EventRegistrationModal';
import { Link } from '@inertiajs/react';
import { ArrowLeft, Calendar, MapPin } from 'lucide-react';
import { renderMarkdown } from '../lib/markdown';

interface EventDetailsPageProps {
  event: Event;
}

const EventDetailsPage: React.FC<EventDetailsPageProps> = ({ event }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleFormSubmit = (details: { name: string; email: string }) => {
    console.log('Registration details:', details, 'for event:', event.title);
  };

  const formatDateRange = (start: string, end: string, timeZone: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
      timeZone: timeZone,
    };

    const startStr = startDate.toLocaleString(undefined, options);
    const endStr = endDate.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: timeZone,
    });

    return `${startStr} - ${endStr}`;
  };

  // Check if the event has ended
  const isEventPast = new Date() > new Date(event.end_time);

  const generateCalendarLink = () => {
    if (!event.start_time || !event.end_time) return '#';

    const formatISODate = (dateStr: string) => new Date(dateStr).toISOString().replace(/-|:|\.\d+/g, '');

    const baseUrl = 'https://www.google.com/calendar/render?action=TEMPLATE';
    const params = new URLSearchParams({
      text: event.title,
      dates: `${formatISODate(event.start_time)}/${formatISODate(event.end_time)}`,
      details: event.plain_text_description,
      location: event.location,
    });

    return `${baseUrl}&${params.toString()}`;
  };

  return (
    <div className='animate-fade-in'>
      <section className='relative py-20 md:py-32 bg-muted overflow-hidden'>
        <div aria-hidden='true' className='absolute -top-24 right-0 w-80 h-80 rounded-full bg-secondary/20 blur-3xl z-0'></div>
        <div className='absolute inset-0 z-0 opacity-20'>
          {(event.image_url || event.media_items?.[0]?.attachment_url) && (
            <img
              src={event.image_url || event.media_items[0].attachment_url}
              alt={event.title}
              className='w-full h-full object-cover'
            />
          )}
          <div className='absolute inset-0 bg-linear-to-t from-background via-background/80 to-background/50'></div>
        </div>
        <div className='container mx-auto px-6 relative z-10 text-center'>
          <Link href='/events' className='flex items-center text-primary font-semibold mb-6 hover:underline mx-auto'>
            <ArrowLeft className='h-5 w-5 mr-2' />
            Back to All Events
          </Link>
          <h1 className='text-4xl md:text-6xl font-bold text-foreground font-heading mb-4 opacity-0 animate-fade-in-up'>
            {event.title}
          </h1>
          <div
            className='flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-lg text-muted-foreground opacity-0 animate-fade-in-up'
            style={{ animationDelay: '0.2s' }}
          >
            <div className='flex items-center gap-2'>
              <Calendar className='h-5 w-5 text-primary' />
              <span>{formatDateRange(event.start_time, event.end_time, event.time_zone)}</span>
            </div>
            <div className='flex items-center gap-2'>
              <MapPin className='h-5 w-5 text-secondary' />
              <span>{event.location}</span>
            </div>
          </div>
        </div>
      </section>

      <section className='py-20'>
        <div className='container mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-12'>
          {/* Main Content */}
          <div className='lg:col-span-2'>
            <h2 className='text-3xl font-bold text-foreground mb-6 font-heading'>About the Event</h2>
            <div
              className='prose prose-lg dark:prose-invert max-w-none text-muted-foreground leading-relaxed'
              dangerouslySetInnerHTML={{ __html: renderMarkdown(event.plain_text_details) }}
            ></div>

            {event.instructors?.length > 0 && (
              <div className='mt-12'>
                <h2 className='text-3xl font-bold text-foreground mb-6 font-heading'>
                  {event.instructors.length > 1 ? 'Speakers & Facilitators' : 'Speaker'}
                </h2>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
                  {event.instructors.map((instructor) => (
                    <div
                      key={instructor.id}
                      className='flex items-start gap-4 bg-card p-5 rounded-lg border border-border'
                    >
                      {instructor.avatar_url ? (
                        <img
                          src={instructor.avatar_url}
                          alt={instructor.name}
                          className='h-16 w-16 rounded-full object-cover shrink-0'
                        />
                      ) : (
                        <div className='h-16 w-16 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xl shrink-0'>
                          {instructor.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <h3 className='font-bold text-foreground'>{instructor.name}</h3>
                        <p className='text-sm text-primary font-medium'>
                          {instructor.role}
                          {instructor.company ? ` · ${instructor.company}` : ''}
                        </p>
                        {instructor.bio && (
                          <p className='text-sm text-muted-foreground mt-2 leading-relaxed'>{instructor.bio}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar — sticks as a unit, then returns to flow at the bottom */}
          <aside className='lg:col-span-1 lg:sticky lg:top-28 lg:self-start space-y-8'>
            <div
              className='bg-card p-6 rounded-lg border border-border shadow-md opacity-0 animate-fade-in-up'
              style={{ animationDelay: '0.5s' }}
            >
              <div className='flex items-center justify-between mb-4'>
                <h3 className='text-xl font-bold text-foreground font-heading'>
                  {isEventPast ? 'Event Ended' : 'Ready to Join?'}
                </h3>
                {!isEventPast && (
                  <a
                    href={generateCalendarLink()}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm font-medium px-3 py-1.5 rounded-md hover:bg-accent group'
                    aria-label='Add to calendar'
                  >
                    <Calendar className='h-4 w-4 group-hover:scale-110 transition-transform' />
                    <span>Add to Calendar</span>
                  </a>
                )}
              </div>
              {isEventPast ? (
                <div className='text-center'>
                  <p className='text-muted-foreground text-sm'>
                    This event has already taken place. Check our{' '}
                    <Link href='/events' className='text-primary hover:underline'>
                      upcoming events
                    </Link>{' '}
                    to see what's next.
                  </p>
                </div>
              ) : (
                <>
                  <p className='text-muted-foreground mb-6'>
                    Secure your spot for this event. We look forward to seeing you there!
                  </p>
                  {event.external_registration_url ? (
                    <a
                      href={event.external_registration_url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='w-full block text-center bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-md text-lg hover:bg-primary/90 transition-all duration-300 transform hover:scale-105'
                    >
                      Register
                    </a>
                  ) : (
                    <button
                      onClick={() => setIsModalOpen(true)}
                      className='w-full bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-md text-lg hover:bg-primary/90 transition-all duration-300 transform hover:scale-105'
                    >
                      Register Now
                    </button>
                  )}
                </>
              )}
            </div>

            {event.agenda && event.agenda.length > 0 && (
              <div>
                <h3 className='text-2xl font-bold text-foreground mb-4 font-heading'>Agenda</h3>
                <ul className='space-y-4'>
                  {event.agenda.map((item, index) => (
                    <li key={index} className='flex items-start p-4 bg-card rounded-lg border border-border'>
                      <div className='bg-primary text-primary-foreground rounded-md px-3 py-1 font-bold text-sm mr-4 whitespace-nowrap'>
                        {item.time}
                      </div>
                      <div className='text-foreground'>{item.topic}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </section>

      {isModalOpen && (
        <EventRegistrationModal
          eventName={event.title}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
};

export default EventDetailsPage;
